import { text } from '../domain/model-utils.js';

export class AssetRepositoryError extends Error {
  constructor(message, details = {}) {
    super(message);
    this.name = 'AssetRepositoryError';
    this.details = details;
  }
}

export class AssetRepository {
  /** @param {{fetchImpl?: typeof fetch, assetBasePath?: string, credentials?: RequestCredentials}} [options] */
  constructor(options = {}) {
    this.fetchImpl = options.fetchImpl || globalThis.fetch?.bind(globalThis);
    this.assetBasePath = options.assetBasePath || '/api/assets';
    this.credentials = options.credentials || 'same-origin';
    this.cache = new Map();
  }

  resolve(assetOrItem) {
    if (typeof assetOrItem === 'string') {
      const looksLikeUrl = /^(?:data:|https?:|blob:|\/)/i.test(assetOrItem);
      return looksLikeUrl
        ? { assetId: '', url: assetOrItem }
        : { assetId: assetOrItem, url: `${this.assetBasePath}/${encodeURIComponent(assetOrItem)}/file` };
    }
    const assetId = text(assetOrItem?.assetId || assetOrItem?.id);
    const url = text(assetOrItem?.fileUrl || assetOrItem?.url, assetId ? `${this.assetBasePath}/${encodeURIComponent(assetId)}/file` : '');
    return { assetId, url };
  }

  /** @param {string | Record<string, any>} assetOrItem */
  async fetchAsset(assetOrItem) {
    const descriptor = this.resolve(assetOrItem);
    if (!descriptor.url) throw new AssetRepositoryError('图片缺少可读取的文件端点。', descriptor);
    if (!this.fetchImpl) throw new AssetRepositoryError('当前环境不支持 fetch。', descriptor);
    const cacheKey = `${descriptor.assetId}|${descriptor.url}`;
    if (this.cache.has(cacheKey)) return this.cache.get(cacheKey);
    const request = this.fetchAndValidate(descriptor).catch(error => {
      this.cache.delete(cacheKey);
      throw error;
    });
    this.cache.set(cacheKey, request);
    return request;
  }

  async fetchAndValidate(descriptor) {
    let response;
    try {
      response = await this.fetchImpl(descriptor.url, { credentials: this.credentials });
    } catch (cause) {
      throw new AssetRepositoryError(`无法读取图片资源：${descriptor.assetId || descriptor.url}`, { ...descriptor, cause });
    }
    if (!response.ok) {
      throw new AssetRepositoryError(`图片资源请求失败（${response.status}）。`, { ...descriptor, status: response.status });
    }
    const headerType = text(response.headers?.get?.('content-type')).split(';')[0].toLowerCase();
    const blob = await response.blob();
    const blobType = text(blob.type).split(';')[0].toLowerCase();
    const mimeType = headerType || blobType;
    if (!mimeType.startsWith('image/')) {
      throw new AssetRepositoryError(`资源不是图片（${mimeType || '未知类型'}）。`, { ...descriptor, mimeType });
    }
    if (!blob.size) throw new AssetRepositoryError('图片资源内容为空。', descriptor);
    return { ...descriptor, mimeType, blob };
  }

  /** @param {string | Record<string, any>} assetOrItem @returns {Promise<Blob>} */
  async getBlob(assetOrItem) {
    return (await this.fetchAsset(assetOrItem)).blob;
  }

  /** @param {string | Record<string, any>} assetOrItem @returns {Promise<ArrayBuffer>} */
  async getArrayBuffer(assetOrItem) {
    return (await this.getBlob(assetOrItem)).arrayBuffer();
  }

  /** @param {string | Record<string, any>} [assetOrItem] */
  clear(assetOrItem) {
    if (assetOrItem === undefined) return this.cache.clear();
    const descriptor = this.resolve(assetOrItem);
    this.cache.delete(`${descriptor.assetId}|${descriptor.url}`);
  }
}
